import React from "react";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useI18n } from "@/i18n/LanguageContext";

export default function Nav({ onMenuToggle }) {
  const { t, code, setLocale, locales } = useI18n();

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-header"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-4">
        {/* Menu toggle */}
        <button
          onClick={onMenuToggle}
          data-testid="menu-toggle-button"
          className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-3.5 py-2.5 transition-colors duration-300 hover:bg-white/[0.08]"
        >
          <Menu className="h-4 w-4 text-white transition-transform duration-300 group-hover:rotate-90" />
          <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/80">
            {t.nav.menu}
          </span>
        </button>

        {/* Right controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-dropdown-trigger"
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-4 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:bg-white/[0.08]"
              >
                {code}
                <span className="text-white/40">▾</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[13rem] border-white/10 bg-[#0b0b0d]/95 backdrop-blur-xl text-white"
              data-testid="lang-dropdown-content"
            >
              {locales.map((l) => (
                <DropdownMenuItem
                  key={l.locale}
                  data-testid={`lang-option-${l.name.replace(/[^a-zA-Z]/g, "")}`}
                  onClick={() => setLocale(l.locale)}
                  className="flex items-center justify-between gap-6 font-mono-accent text-xs uppercase tracking-widest focus:bg-white/10 focus:text-white cursor-pointer"
                >
                  <span className="text-white">{l.code}</span>
                  <span className="text-[10px] normal-case tracking-wide text-white/40">
                    {l.name}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="https://orbtech-io-ai.lovable.app/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="beta-signup-button"
            className="rounded-full bg-[#a855f7] px-6 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#9333ea] active:scale-[0.97]"
          >
            {t.nav.betaSignup}
          </a>
        </div>
      </div>
    </motion.header>
  );
}
